import { useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit, MdContentCopy, MdDevices } from "react-icons/md";
import Card from "components/card";
import { api } from "api";

const LICENSE_TYPES: Record<string, string> = { free: "Ucretsiz", demo: "Demo (eski)", yearly: "Yillik", lifetime: "Omur Boyu" };

const Licenses = () => {
  const [licenses, setLicenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState<any>(null);
  const [createForm, setCreateForm] = useState({ license_type: "yearly", max_devices: 3, count: 1, customer_name: "", customer_email: "", customer_phone: "", customer_company: "", note: "" });
  const [showEdit, setShowEdit] = useState<any>(null);
  const [editForm, setEditForm] = useState({ license_type: "", max_devices: 3, customer_name: "", customer_email: "", customer_phone: "", customer_company: "", note: "", is_active: true, end_date: "" });
  const [msg, setMsg] = useState("");

  const load = () => {
    setLoading(true);
    api.getLicenses(filter || undefined).then(setLicenses).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const handleCreate = async () => {
    try {
      if (createForm.count > 1) {
        await api.createLicensesBulk(createForm);
      } else {
        await api.createLicense(createForm);
      }
      setShowCreate(false);
      setCreateForm({ license_type: "yearly", max_devices: 3, count: 1, customer_name: "", customer_email: "", customer_phone: "", customer_company: "", note: "" });
      load();
      setMsg("Lisans olusturuldu!");
      setTimeout(() => setMsg(""), 3000);
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Bu lisansi silmek istediginize emin misiniz?")) return;
    await api.deleteLicense(id);
    load();
  };

  const handleRemoveDevice = async (licId: number, devId: number) => {
    if (!window.confirm("Bu cihazi kaldirmak istediginize emin misiniz?")) return;
    await api.removeDevice(licId, devId);
    const updated = await api.getLicense(licId);
    setShowDetail(updated);
    load();
  };

  const openEdit = (lic: any) => {
    setEditForm({
      license_type: lic.license_type,
      max_devices: lic.max_devices,
      customer_name: lic.customer_name || "",
      customer_email: lic.customer_email || "",
      customer_phone: lic.customer_phone || "",
      customer_company: lic.customer_company || "",
      note: lic.note || "",
      is_active: lic.is_active,
      end_date: lic.end_date || "",
    });
    setShowEdit(lic);
  };

  const handleEdit = async () => {
    try {
      const data = { ...editForm, end_date: editForm.end_date || null };
      await api.updateLicense(showEdit.id, data);
      setShowEdit(null);
      load();
      setMsg("Lisans guncellendi!");
      setTimeout(() => setMsg(""), 3000);
    } catch (e: any) {
      alert(e.message || "Bir hata olustu");
    }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setMsg("Lisans key kopyalandi!");
    setTimeout(() => setMsg(""), 2000);
  };

  const isExpired = (end_date: string | null) => end_date && new Date(end_date) < new Date();

  return (
    <div>
      {msg && (
        <div className="mb-4 rounded-xl bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
          {msg}
        </div>
      )}

      {/* Header */}
      <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-2">
          {["", "active", "expired", "unassigned"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${filter === f ? "bg-brand-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-white"}`}>
              {f === "" ? "Tumu" : f === "active" ? "Aktif" : f === "expired" ? "Suresi Dolan" : "Atanmamis"}
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition">
          <MdAdd className="h-5 w-5" /> Yeni Lisans
        </button>
      </div>

      {/* Table */}
      <Card extra="mt-5 p-4">
        {loading ? (
          <p className="p-4 text-center text-gray-500">Yukleniyor...</p>
        ) : licenses.length === 0 ? (
          <p className="p-4 text-center text-gray-500">Lisans bulunamadi</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-navy-700">
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Lisans Key</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Musteri</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Tip</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Durum</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Cihaz</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Bitis</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Islem</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((lic) => (
                  <tr key={lic.id} className="border-b border-gray-100 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700/50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-navy-700 dark:text-white">{lic.key}</span>
                        <button onClick={() => copyKey(lic.key)} className="text-gray-400 hover:text-brand-500"><MdContentCopy className="h-4 w-4" /></button>
                      </div>
                      {lic.user_id && <p className="text-xs text-gray-500 mt-0.5">Kullanici #{lic.user_id}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {lic.customer_name ? (
                        <div>
                          <p className="text-sm font-medium text-navy-700 dark:text-white">{lic.customer_name}</p>
                          {lic.customer_company && <p className="text-xs text-gray-500">{lic.customer_company}</p>}
                          {lic.customer_phone && <p className="text-xs text-gray-400">{lic.customer_phone}</p>}
                        </div>
                      ) : <span className="text-xs text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{LICENSE_TYPES[lic.license_type] || lic.license_type}</td>
                    <td className="px-4 py-3">
                      {!lic.is_active ? (
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">Pasif</span>
                      ) : isExpired(lic.end_date) ? (
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-600">Suresi Dolmus</span>
                      ) : (
                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-600">Aktif</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{lic.devices?.length || 0}/{lic.max_devices}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {lic.end_date ? new Date(lic.end_date).toLocaleDateString("tr-TR") : "Sinirsiz"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(lic)} className="text-brand-500 hover:text-brand-700"><MdEdit className="h-5 w-5" /></button>
                        <button onClick={() => setShowDetail(lic)} className="text-gray-400 hover:text-gray-600"><MdDevices className="h-5 w-5" /></button>
                        <button onClick={() => handleDelete(lic.id)} className="text-red-400 hover:text-red-600"><MdDelete className="h-5 w-5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">Yeni Lisans Olustur</h3>
            <div className="max-h-[70vh] overflow-y-auto space-y-4 pr-1">
              <p className="text-xs font-bold uppercase text-gray-500 tracking-wider">Musteri Bilgileri</p>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ad Soyad</label>
                <input type="text" value={createForm.customer_name} onChange={(e) => setCreateForm({ ...createForm, customer_name: e.target.value })}
                  placeholder="Musteri adi" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <input type="email" value={createForm.customer_email} onChange={(e) => setCreateForm({ ...createForm, customer_email: e.target.value })}
                  placeholder="musteri@email.com" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Telefon</label>
                <input type="tel" value={createForm.customer_phone} onChange={(e) => setCreateForm({ ...createForm, customer_phone: e.target.value })}
                  placeholder="05XX XXX XX XX" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sirket</label>
                <input type="text" value={createForm.customer_company} onChange={(e) => setCreateForm({ ...createForm, customer_company: e.target.value })}
                  placeholder="Sirket adi" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>

              <div className="h-px bg-gray-200 dark:bg-navy-600" />
              <p className="text-xs font-bold uppercase text-gray-500 tracking-wider">Lisans Ayarlari</p>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Lisans Tipi</label>
                <select value={createForm.license_type} onChange={(e) => setCreateForm({ ...createForm, license_type: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white">
                  <option value="demo">Demo (14 gun)</option>
                  <option value="yearly">Yillik</option>
                  <option value="lifetime">Omur Boyu</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Cihaz Sayisi</label>
                <input type="number" min={1} max={10} value={createForm.max_devices}
                  onChange={(e) => setCreateForm({ ...createForm, max_devices: Number(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Adet (toplu olusturma)</label>
                <input type="number" min={1} max={100} value={createForm.count}
                  onChange={(e) => setCreateForm({ ...createForm, count: Number(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Not</label>
                <input type="text" value={createForm.note} onChange={(e) => setCreateForm({ ...createForm, note: e.target.value })}
                  placeholder="Opsiyonel not" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowCreate(false)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300">Iptal</button>
              <button onClick={handleCreate} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">Olustur</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDetail(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Lisans Detayi</h3>
            <p className="font-mono text-lg text-brand-500 mb-4">{showDetail.key}</p>

            {showDetail.customer_name && (
              <div className="mb-4 rounded-xl bg-lightPrimary p-4 dark:bg-navy-700">
                <p className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">Musteri Bilgileri</p>
                <p className="font-medium text-navy-700 dark:text-white">{showDetail.customer_name}</p>
                {showDetail.customer_company && <p className="text-sm text-gray-600">{showDetail.customer_company}</p>}
                {showDetail.customer_email && <p className="text-sm text-gray-500">{showDetail.customer_email}</p>}
                {showDetail.customer_phone && <p className="text-sm text-gray-500">{showDetail.customer_phone}</p>}
              </div>
            )}

            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300 mb-4">
              <div className="flex justify-between"><span>Tip:</span><span className="font-bold">{LICENSE_TYPES[showDetail.license_type]}</span></div>
              <div className="flex justify-between"><span>Baslangic:</span><span>{new Date(showDetail.start_date).toLocaleDateString("tr-TR")}</span></div>
              <div className="flex justify-between"><span>Bitis:</span><span>{showDetail.end_date ? new Date(showDetail.end_date).toLocaleDateString("tr-TR") : "Sinirsiz"}</span></div>
              <div className="flex justify-between"><span>Max Cihaz:</span><span>{showDetail.max_devices}</span></div>
              {showDetail.note && <div className="flex justify-between"><span>Not:</span><span>{showDetail.note}</span></div>}
            </div>

            <h4 className="font-bold text-navy-700 dark:text-white mb-2">Kayitli Cihazlar ({showDetail.devices?.length || 0})</h4>
            {showDetail.devices?.length > 0 ? (
              <div className="space-y-2">
                {showDetail.devices.map((d: any) => (
                  <div key={d.id} className="flex items-center justify-between rounded-xl bg-lightPrimary p-3 dark:bg-navy-700">
                    <div>
                      <p className="font-medium text-navy-700 dark:text-white">{d.device_name || "Bilinmeyen Cihaz"}</p>
                      <p className="text-xs text-gray-500">{d.platform} - Son: {new Date(d.last_seen).toLocaleDateString("tr-TR")}</p>
                    </div>
                    <button onClick={() => handleRemoveDevice(showDetail.id, d.id)} className="text-red-400 hover:text-red-600"><MdDelete className="h-5 w-5" /></button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Henuz kayitli cihaz yok</p>
            )}

            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowDetail(null)} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">Kapat</button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowEdit(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-2">Lisans Duzenle</h3>
            <p className="font-mono text-sm text-brand-500 mb-4">{showEdit.key}</p>
            <div className="max-h-[70vh] overflow-y-auto space-y-4 pr-1">
              <p className="text-xs font-bold uppercase text-gray-500 tracking-wider">Musteri Bilgileri</p>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ad Soyad</label>
                <input type="text" value={editForm.customer_name} onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <input type="email" value={editForm.customer_email} onChange={(e) => setEditForm({ ...editForm, customer_email: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Telefon</label>
                <input type="tel" value={editForm.customer_phone} onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sirket</label>
                <input type="text" value={editForm.customer_company} onChange={(e) => setEditForm({ ...editForm, customer_company: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>

              <div className="h-px bg-gray-200 dark:bg-navy-600" />
              <p className="text-xs font-bold uppercase text-gray-500 tracking-wider">Lisans Ayarlari</p>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Lisans Tipi</label>
                <select value={editForm.license_type} onChange={(e) => setEditForm({ ...editForm, license_type: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white">
                  <option value="demo">Demo (14 gun)</option>
                  <option value="yearly">Yillik</option>
                  <option value="lifetime">Omur Boyu</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Cihaz Sayisi</label>
                <input type="number" min={1} max={10} value={editForm.max_devices}
                  onChange={(e) => setEditForm({ ...editForm, max_devices: Number(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Bitis Tarihi</label>
                <input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Durum</label>
                <select value={editForm.is_active ? "true" : "false"} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === "true" })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white">
                  <option value="true">Aktif</option>
                  <option value="false">Pasif</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Not</label>
                <input type="text" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowEdit(null)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleEdit} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">Guncelle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Licenses;
